import { Theme } from '@chronosphereio/chrono-ui';
import { CSSProperties } from 'react';
import {
  ContentDimensions,
  getLegendMode,
  LegendMode,
  LegendOptionsBase,
  LegendPositions,
  LegendSize,
  LegendSizeStandard,
  PanelLayoutStrategy,
} from '@/core';
import { DEFAULT_LEGEND_DENSITY, LegendProps, shouldRenderCompactListLegend } from '../../Legend';
import { LIST_LEGEND_ITEM_HEIGHT } from '../../Legend/ListLegendItem';
import { TABLE_LEGEND_ROW_HEIGHT, TableLegendColumns } from '../../Legend/TableLegend';
import { ContentWithLegendCollapseProps } from '../ContentWithLegendCollapse';
import { ContentWithLegendGroupByProps } from '../ContentWithLegendGroupBy';

export interface ContentWithLegendQuickFilterProps {
  /**
   * Determines if the quick filter should be shown. The quick filter is currently only supported for
   * bottom-positioned legends, so this value will be treated as `false` for right-positioned legends.
   */
  show?: boolean;

  /**
   * Placeholder shown in the quick filter input.
   *
   * @default 'Filter series'
   */
  placeholder?: string;

  /**
   * Callback that includes the latest value of the quick filter. Use this to implement filtering of results based on
   * the filter. Note that this callback is debounced to avoid performance issues.
   */
  onChange?: (newFilter: string) => void;
}

export interface ContentWithLegendCollapsibleLegendProps
  extends Pick<ContentWithLegendCollapseProps, 'title' | 'show'> {
  /**
   * Initial state for the legend collapsing.
   *
   * Legend collapsing is currently an uncontrolled feature. Please reach out at http://go/vf-help if you have use
   * cases that would benefit from a controlled version of this feature.
   */
  defaultIsCollapsed?: boolean;
}

export interface ContentWithLegendToggleModeProps {
  mode: LegendMode;
  show?: boolean;
  onChange?: (newMode: LegendMode) => void;
}

export interface ContentWithLegendProps extends ContentDimensions {
  /**
   * Width of the overall component layout in pixels.
   */
  width: number;
  /**
   * Height of overall component layout in pixels.
   */
  height: number;
  /**
   * Child content to render next to the legend. May be a react node or a
   * function that returns a react node. The function provides the expected
   * height and width for the content, which can be useful for passing down
   * to chart components.
   */
  children: React.ReactNode | (({ width, height }: ContentDimensions) => React.ReactNode);

  /**
   * Size used for the legend.
   *
   * @default 'Medium'
   */
  legendSize?: LegendSize;

  /**
   * Total number of legend items that may be displayed in the legend. This total is important to
   * specify when using the following configuration to avoid the legend height shifting as legend items
   * are filtered.
   * - `quickFilterProps.show` is true.
   */
  totalLegendItems?: number;

  /**
   * Props to configure the legend. If not set, the content is rendered without
   * a legend.
   */
  legendProps?: Omit<LegendProps, 'width' | 'height'>;

  quickFilterProps?: ContentWithLegendQuickFilterProps;
  toggleModeProps?: Partial<ContentWithLegendToggleModeProps>;

  /**
   * Space to put between the children and the legend in pixels.
   */
  spacing?: number;

  /**
   * Minimum width required for the content specified by the `children` prop.
   * If this width cannot be maintained with a right positioned legend, the
   * legend will not be shown.
   */
  minChildrenWidth?: number;

  /**
   * Minimum height required for the content specified by the `children` prop.
   * If this width cannot be maintained with a bottom positioned legend, the
   * legend will not be shown.
   */
  minChildrenHeight?: number;

  /**
   * When true the legendSize will behave consistently reguardless of the number of legend items.
   * When not enabled, the legend will shrink to fit the number of items when the specified size is larger
   * than needed to show all items.
   *
   * Recommend setting to `false` when trying to optimize for screen real estate for charts.
   * Recommend setting to `true` when optimizing for consistent layouts across charts on a page regardless
   * of the number of items they have.
   *
   * @default false
   */
  enableConsistentLegendSize?: boolean;

  layoutStrategy?: PanelLayoutStrategy;

  /**
   * Props that control collapsing of `Bottom`-positioned legends.
   */
  collapsibleLegendProps?: ContentWithLegendCollapsibleLegendProps;

  /**
   * Props that control grouping behavior of the legend.
   */
  groupByProps?: ContentWithLegendGroupByProps;
}

export interface ContentWithLegendLayoutOpts
  extends Required<
      Omit<
        ContentWithLegendProps,
        | 'children'
        | 'legendProps'
        | 'quickFilterProps'
        | 'toggleModeProps'
        | 'totalLegendItems'
        | 'collapsibleLegendProps'
        | 'groupByProps'
      >
    >,
    Pick<ContentWithLegendProps, 'totalLegendItems'> {
  legendProps?: ContentWithLegendProps['legendProps'];
  theme: Theme;
  showQuickFilter?: boolean;
  showToggleMode?: boolean;
  showGroupBy?: boolean;
  showCollapsibleLegend?: boolean;
  isCollapsed?: boolean;
}

export interface ContentWithLegendLayout {
  component: ContentDimensions;
  legend: ContentDimensions & {
    show: boolean;
    mode: LegendMode;
  };
  content: ContentDimensions;
  toolbar: ContentDimensions & {
    show: boolean;
  };
  margin: {
    right: number;
    bottom: number;
  };
  flexDirection: CSSProperties['flexDirection'];
  isCompactListLegend: boolean;
}

type LegendSizeConfig = Record<LegendSizeStandard, Record<LegendPositions, number>>;

/**
 * Ensures the provided number of rows includes a half row. We want to display a half row
 * peeking out to make it obvious that the user can scroll when not all rows fit.
 */
function displayHalfRow(numRows: number): number {
  return Math.floor(numRows) + 0.5;
}
/** Normalizes pixel-based legend size into rows */
export function convertPixelsToRows(sizeInPixels: number, rowHeight: number, headerRowSize: number): number {
  return displayHalfRow((sizeInPixels - rowHeight * 0.5 - headerRowSize * rowHeight) / rowHeight);
}

export function calculateLegendSize(
  size: LegendSizeStandard,
  position: LegendPositions,
  heightInRows: number,
  width: number
): number {
  const sizeConfigs: LegendSizeConfig = {
    // Note: We do not currently expose the Large case in dashboards, but left it here in case we ever
    // want to add it back and/or if a product-based use case might still want it.
    Large: {
      // 9.5 rows for list mode (1 row will be added for the header in table mode).
      // Value to be multiplied by row height in pixels.
      Bottom: 9.5,

      // Pixel value
      Right: 400,
    },
    Medium: {
      // Rows for list mode (1 row will be added for the header in table mode).
      // Value to be multiplied by row height in pixels.
      Bottom: Math.min(6.5, Math.max(3.5, displayHalfRow(heightInRows * 0.3))),

      // Pixel value
      Right: Math.min(350, Math.max(200, width * 0.25)),
    },
    Small: {
      // Rows for list mode (1 row will be added for the header in table mode).
      // Value to be multiplied by row height in pixels.
      Bottom: Math.min(2.5, Math.max(1.5, displayHalfRow(heightInRows * 0.2))),

      // Pixel value
      Right: Math.min(200, Math.max(120, width * 0.15)),
    },
  };

  return sizeConfigs[size]?.[position];
}

const TOOLBAR_HEIGHT = 40;

function isStandardLegendSize(legendSize: LegendSize): legendSize is LegendSizeStandard {
  /** we just need to confirm whether an actual value is returned, so the arguments other than legendSize don't matter */
  return !!calculateLegendSize(legendSize as LegendSizeStandard, 'Bottom', 0, 0);
}

/**
 * Iterates the configured table columns and calculates the total width across
 * them.
 */
function calculateTableColumnsWidth(tableColumns: TableLegendColumns): number {
  return tableColumns.reduce((total, col) => {
    if (typeof col.width === 'number') {
      total += col.width;
    }
    return total;
  }, 0);
}

type CalculateLegendHeightProps = Pick<LegendOptionsBase, 'mode' | 'position'> &
  Pick<LegendProps, 'density'> &
  Pick<
    ContentWithLegendLayoutOpts,
    'legendSize' | 'height' | 'enableConsistentLegendSize' | 'showToggleMode' | 'isCollapsed'
  > & {
    totalLegendItems: number;
  };

/**
 * Calculates the height to use for the legend. In cases where more items are available than can
 * be shown, these calculations optimize for the last legend item to be half shown, which helps
 * indicate to the user that they can scroll to see more.
 */
function calculateLegendHeight({
  mode,
  density = DEFAULT_LEGEND_DENSITY,
  position,
  legendSize,
  height,
  totalLegendItems,
  showToggleMode,
  enableConsistentLegendSize,
  isCollapsed,
}: CalculateLegendHeightProps): number {
  // Use measurements for `Table` mode when in that mode or when `showToggleMode` is enabled. We use `Table`-based
  // measurements when the user can toggle to keep a consistent height as the user toggles. Otherwise the content
  // would shift a little when toggling, which has a poor UX.
  const useTableMeasurements = mode === 'Table' || showToggleMode;

  const headerRowSize = useTableMeasurements ? 1 : 0;
  const rowHeight = useTableMeasurements ? TABLE_LEGEND_ROW_HEIGHT[density] : LIST_LEGEND_ITEM_HEIGHT;

  if (position === 'Bottom') {
    if (isCollapsed) {
      return 0;
    }

    let bottomLegendSize: number;
    if (isStandardLegendSize(legendSize)) {
      bottomLegendSize = calculateLegendSize(legendSize, 'Bottom', convertPixelsToRows(height, rowHeight, 0), 0);
    } else if (typeof legendSize == 'number') {
      bottomLegendSize = convertPixelsToRows(legendSize, rowHeight, headerRowSize);
    } else if (legendSize.endsWith('%')) {
      const percent = parseFloat(legendSize.replace('%', ''));
      bottomLegendSize = convertPixelsToRows(height * (percent / 100), rowHeight, headerRowSize);
    } else {
      throw new Error('Invalid legend size.');
    }

    const headerPadding = useTableMeasurements ? 2 : 0;

    const consistentRowsToShow = bottomLegendSize + headerRowSize;
    // If there are no legend items, we still want to show at least one row to show the no data message
    const legendItemRows = totalLegendItems === 0 ? 1 : totalLegendItems;
    const rowsToShow = enableConsistentLegendSize
      ? consistentRowsToShow
      : // Use the smaller of the size-based row count or the number of legend items.
        Math.min(consistentRowsToShow, legendItemRows + headerRowSize);

    // Add extra height to accommodate the scrollbar when only one series is present,
    // preventing it from overlapping the single legend item.
    if (rowsToShow === 2) {
      const scrollBarHeight = 16;
      return rowsToShow * rowHeight + headerPadding + scrollBarHeight;
    }

    return rowsToShow * rowHeight + headerPadding;
  }

  // Position right.
  const visibleRowsWithContentHeight = height / rowHeight;

  // Use a full fraction to separate the whole number from the decimal.
  const visibleRowsFraction = visibleRowsWithContentHeight % 1;
  const visibleRowsWhole = visibleRowsWithContentHeight - visibleRowsFraction;

  // Use the fraction limited to a single decimal point to avoid overcorrecting in edge cases
  // where there's a small deviation from 0.5.
  const visibleRowsFractionOneDecimal = parseFloat(visibleRowsFraction.toFixed(1));

  if (visibleRowsFractionOneDecimal === 0.5 || visibleRowsWithContentHeight === totalLegendItems) {
    // The specified height shows rows with a half item at the end OR shows all items, so we do
    // not need a half item to communicate the ability to scroll. Use the height as is.
    return height;
  }

  if (visibleRowsFractionOneDecimal < 0.5) {
    // The specified height will not show a half item. Decrease the height to
    // show slightly less and ensure the last item is half height.
    return (visibleRowsWhole - 0.5) * rowHeight;
  }

  // The specfied height will show more than half of the last item. Decrease the height to
  // ensure the last item is half height.
  return (visibleRowsWhole + 0.5) * rowHeight;
}

type CalculateLegendWidthProps = Pick<LegendOptionsBase, 'position'> &
  Pick<ContentWithLegendLayoutOpts, 'legendSize' | 'width'> & {
    columnsWidth: number;
  };

function calculateLegendWidth({ legendSize, position, width, columnsWidth }: CalculateLegendWidthProps): number {
  let rightLegendSize: number;
  if (isStandardLegendSize(legendSize)) {
    rightLegendSize = calculateLegendSize(legendSize, 'Right', 0, width);
  } else if (typeof legendSize == 'number') {
    rightLegendSize = legendSize;
  } else if (legendSize.endsWith('%')) {
    const percent = parseFloat(legendSize.replace('%', ''));
    rightLegendSize = width * (percent / 100);
  } else {
    throw new Error('Invalid legend size.');
  }

  let legendWidth: number;
  if (position === 'Bottom') {
    legendWidth = width;
  } else if (isStandardLegendSize(legendSize)) {
    legendWidth = rightLegendSize + columnsWidth;
  } else {
    legendWidth = rightLegendSize;
  }

  return legendWidth;
}

/**
 * Returns information for laying out content alongside a legend.
 */
export function getContentWithLegendLayout({
  width,
  height,
  legendProps,
  legendSize,
  totalLegendItems: initTotalLegendItems,
  minChildrenHeight,
  minChildrenWidth,
  spacing,
  showQuickFilter,
  showToggleMode,
  enableConsistentLegendSize,
  layoutStrategy,
  showCollapsibleLegend,
  isCollapsed,
  showGroupBy,
}: ContentWithLegendLayoutOpts): ContentWithLegendLayout {
  const legendOptions = legendProps?.options;
  const hasLegend = !!legendOptions;

  const mode = getLegendMode(legendOptions?.mode);

  const noLegendLayout: ContentWithLegendLayout = {
    component: {
      width,
      height,
    },
    legend: {
      show: false,
      mode,
      width: 0,
      height: 0,
    },
    toolbar: {
      show: false,
      width: 0,
      height: 0,
    },
    content: {
      width,
      height,
    },
    margin: {
      right: 0,
      bottom: 0,
    },
    isCompactListLegend: false,
    flexDirection: 'column',
  };

  if (!hasLegend) {
    return noLegendLayout;
  }

  const { position } = legendOptions;

  const showToolbar =
    position === 'Bottom' && (!!showQuickFilter || !!showToggleMode || !!showCollapsibleLegend || !!showGroupBy);
  const toolbarHeight = showToolbar ? TOOLBAR_HEIGHT : 0;

  const tableColumns = legendProps?.tableProps?.columns || [];
  const columnsWidth = mode === 'Table' ? calculateTableColumnsWidth(tableColumns) : 0;

  // If provided, we use the specific total legend items. Otherwise, infer it from the legend data.
  // The specified number is useful when using the "quick filter" to avoid the total legend items
  // changing as the results are filtered.
  const totalLegendItems = initTotalLegendItems ?? legendProps.data.length;

  const density = legendProps.density || DEFAULT_LEGEND_DENSITY;

  const legendWidth = calculateLegendWidth({
    legendSize,
    position,
    width,
    columnsWidth,
  });

  const legendHeight = calculateLegendHeight({
    mode,
    position,
    legendSize,
    height,
    totalLegendItems,
    showToggleMode,
    enableConsistentLegendSize,
    density,
    isCollapsed,
  });

  let legendHeightForCalculatingContent = legendHeight;
  if (layoutStrategy === 'flex-height') {
    // When laying out flexibly, we want to size the content based on what it would look like if the legend
    // took up the full space it needs (instead of growing the content to fill the space if the
    // legend was smaller).
    legendHeightForCalculatingContent = calculateLegendHeight({
      mode,
      position,
      legendSize,
      height,
      totalLegendItems,
      showToggleMode,
      enableConsistentLegendSize: true,
      density,
      isCollapsed: false,
    });
  }

  const contentWidth = position === 'Right' ? width - legendWidth - spacing : width;
  const contentHeight =
    position === 'Bottom' ? height - legendHeightForCalculatingContent - toolbarHeight - spacing : height;

  if (
    (position === 'Right' && contentWidth < minChildrenWidth) ||
    (position === 'Bottom' && contentHeight < minChildrenHeight)
  ) {
    // Legend does not fit. Just show the content.
    return noLegendLayout;
  }

  let componentHeight = height;
  if (layoutStrategy === 'flex-height') {
    // When laying out flexibly, calculate height based on the actual space taken up, so it can shrink if it doesn't need the
    // full space.
    componentHeight = legendHeight + toolbarHeight + contentHeight + spacing;
  }

  return {
    component: {
      height: componentHeight,
      width,
    },
    legend: {
      width: legendWidth,
      height: legendHeight,
      show: !isCollapsed,
      mode,
    },
    content: {
      width: contentWidth,
      height: contentHeight,
    },
    toolbar: {
      width: legendWidth,
      height: toolbarHeight,
      show: showToolbar,
    },
    margin: {
      right: position === 'Right' ? spacing : 0,
      bottom: position === 'Bottom' ? spacing : 0,
    },
    flexDirection: position === 'Right' ? 'row' : 'column',
    isCompactListLegend: shouldRenderCompactListLegend({
      mode,
      position,
      totalLegendItems,

      // Disable compact legend when using toolbar features (e.g. quick search, toggle mode) because the features do not
      // play nicely together because the compact legend uses a flexible layout that would jump up/down in size as the
      // content changes during filtering.
      disableCompactLegend: showToolbar,
    }),
  };
}
